import React, { useEffect, useRef, useState, useMemo } from 'react'
import styled from 'styled-components'
import { getSleepSignalsWeighting, updateSleepSignalsWeighting } from '../../api'
import { Table, Button, Input, message } from 'antd'
import saveIcon from '../../asserts/icon/saveIcon.svg'
import CancelIcon from '../../asserts/icon/CancelIcon.svg'
import editIcon from '../../asserts/icon/icon-profile-edit.svg'
import Reset from '../../asserts/icon/Reset.svg'
import { SLEEP_SIGNALS_WEIGHTING_TYPE } from '../../utils/constant'

const Container = styled.div`
  .header-tool {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .cardList {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
  }

  .ant-card {
    height: 110px;
    border-radius: 12px;
    box-shadow: 4px 4px 20px 0px rgba(0, 0, 0, 0.05);
  }
  .ant-card-body {
    height: 100%;
  }

  .card-contents {
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
    height: 100%;

    img {
      postion: absolute;
      top: 0px;
    }

    .text-contents {
      padding-left: 16px;
      display: flex;
      flex-direction: column;
    }

    button {
      position: absolute;
      right: 24px;
    }

    .title {
      font-size: 18px;
      color: #29303e;
      font-family: 'Gilroy';
      font-weight: 500;
    }
    .subtitle {
      font-size: 12px;
      color: #a5abb2;
      font-family: 'Gilroy';
      font-weight: 500;
    }
  }
`

export const SleepSignalsWeightings = () => {
    const [weights, setWeights] = useState([]);
    const [hsdKeys, setHsdKeys] = useState([]);
    const [algoKeys, setAlgoKeys] = useState([]);
    // Editing state for HSD and Sleep Algorithm tables
    const [hsdEditingKey, setHsdEditingKey] = useState('');
    const [hsdEditingCache, setHsdEditingCache] = useState({});
    const [algoEditingKey, setAlgoEditingKey] = useState('');
    const [algoEditingCache, setAlgoEditingCache] = useState({});
    const hsdEditingKeyRef = useRef(hsdEditingKey);
    const algoEditingKeyRef = useRef(algoEditingKey);

    const fetchWeighting = async () => {
        // Fetch weighting data
        const response = await getSleepSignalsWeighting();
        if (response) {
            setWeights(response);
            setHsdKeys(Object.keys(response[SLEEP_SIGNALS_WEIGHTING_TYPE.HSD_SCORES] || {}));
            setAlgoKeys(Object.keys(response[SLEEP_SIGNALS_WEIGHTING_TYPE.SLEEP_ALGORITHM] || {}));
        }
    }
    // Memoized columns for HSD Scores
    const columnsHSDScores = useMemo(() =>
        hsdKeys.map((key) => ({
            title: key.replace(/_/g, ' ').toUpperCase(),
            dataIndex: key,
            key: key,
            align: 'center',
            render: (text, record) =>
                hsdEditingKey === record.key ? (
                    <Input
                        value={hsdEditingCache[key] !== undefined ? hsdEditingCache[key] : text}
                        onChange={e => handleHsdInputChange(e, key)}
                        style={{ width: 80 }}
                    />
                ) : (
                    text
                )
        })),
        [hsdKeys, hsdEditingKey, hsdEditingCache]
    );

    // Memoized columns for Sleep Algorithm
    const columnsSleepAlgorithm = useMemo(() =>
        algoKeys.map((key) => ({
            title: key.replace(/_/g, ' ').toUpperCase(),
            dataIndex: key,
            key: key,
            align: 'center',
            render: (text, record) =>
                algoEditingKey === record.key ? (
                    <Input
                        value={algoEditingCache[key] !== undefined ? algoEditingCache[key] : text}
                        onChange={e => handleAlgoInputChange(e, key)}
                        style={{ width: 80 }}
                    />
                ) : (
                    text
                )
        })),
        [algoKeys, algoEditingKey, algoEditingCache]
    );
    // HSD Table Handlers
    const handleHsdEdit = (key) => {
        setHsdEditingKey(key);
        hsdEditingKeyRef.current = key;
        // Set initial cache to current row values
        const row = weights[SLEEP_SIGNALS_WEIGHTING_TYPE.HSD_SCORES] || {};
        setHsdEditingCache({ ...row });
    };
    const handleHsdSave = async (key) => {
        const updatedRow = { ...hsdEditingCache };
        for (const k in updatedRow) {
            if (updatedRow[k] === '' || isNaN(updatedRow[k]) || Number(updatedRow[k]) < 0) {
                message.error('All values must be non-negative numbers.');
                return;
            }
        }
        try {
            await updateSleepSignalsWeighting({
                type: SLEEP_SIGNALS_WEIGHTING_TYPE.HSD_SCORES,
                value: updatedRow
            });
            setWeights(prev => ({
                ...prev,
                [SLEEP_SIGNALS_WEIGHTING_TYPE.HSD_SCORES]: { ...updatedRow }
            }));
            setHsdEditingKey('');
            hsdEditingKeyRef.current = '';
            setHsdEditingCache({});
            message.success('HSD Scores updated!');
        } catch (err) {
            message.error('Failed to update HSD Scores!');
        }
    };
    const handleHsdCancel = () => {
        setHsdEditingKey('');
        hsdEditingKeyRef.current = '';
        setHsdEditingCache({});
    };
    const handleHsdInputChange = (e, field) => {
        const value = e.target.value;
        const regex = /^\d*(\.\d{0,2})?$/;
        if (!regex.test(value)) {
            message.error('Only numbers with up to 2 decimal places are allowed');
            return;
        }
        if (value !== '' && parseFloat(value) < 0) {
            message.error('Negative values are not allowed');
            return;
        }
        setHsdEditingCache(prev => ({ ...prev, [field]: value }));
    };

    // HSD Reset Handler
    const handleHsdReset = (key) => {
        // Optionally, you can reset to some default values or refetch from API
        fetchWeighting();
        setHsdEditingKey('');
        hsdEditingKeyRef.current = '';
        setHsdEditingCache({});
        message.success('HSD Scores reset!');
    };

    // Sleep Algorithm Table Handlers
    const handleAlgoEdit = (key) => {
        setAlgoEditingKey(key);
        algoEditingKeyRef.current = key;
        // Set initial cache to current row values
        const row = weights[SLEEP_SIGNALS_WEIGHTING_TYPE.SLEEP_ALGORITHM] || {};
        setAlgoEditingCache({ ...row });
    };
    const handleAlgoSave = async (key) => {
        const updatedRow = { ...algoEditingCache };
        for (const k in updatedRow) {
            if (updatedRow[k] === '' || isNaN(updatedRow[k]) || Number(updatedRow[k]) < 0) {
                message.error('All values must be non-negative numbers.');
                return;
            }
        }
        try {
            await updateSleepSignalsWeighting({
                type: SLEEP_SIGNALS_WEIGHTING_TYPE.SLEEP_ALGORITHM,
                value: updatedRow
            });
            setWeights(prev => ({
                ...prev,
                [SLEEP_SIGNALS_WEIGHTING_TYPE.SLEEP_ALGORITHM]: { ...updatedRow }
            }));
            setAlgoEditingKey('');
            algoEditingKeyRef.current = '';
            setAlgoEditingCache({});
            message.success('Sleep Algorithm updated!');
        } catch (err) {
            message.error('Failed to update Sleep Algorithm!');
        }
    };
    const handleAlgoCancel = () => {
        setAlgoEditingKey('');
        algoEditingKeyRef.current = '';
        setAlgoEditingCache({});
    };
    const handleAlgoInputChange = (e, field) => {
        const value = e.target.value;
        const regex = /^\d*(\.\d{0,2})?$/;
        if (!regex.test(value)) {
            message.error('Only numbers with up to 2 decimal places are allowed');
            return;
        }
        if (value !== '' && parseFloat(value) < 0) {
            message.error('Negative values are not allowed');
            return;
        }
        setAlgoEditingCache(prev => ({ ...prev, [field]: value }));
    };

    // Sleep Algorithm Reset Handler
    const handleAlgoReset = (key) => {
        // Optionally, you can reset to some default values or refetch from API
        fetchWeighting();
        setAlgoEditingKey('');
        algoEditingKeyRef.current = '';
        algoEditingCache.current = {};
        message.success('Sleep Algorithm reset!');
    };

    useEffect(() => {
        fetchWeighting()
    }, [])

    // Action columns for HSD and Algorithm tables
    const HSD_ACTION_COLUMN = {
        title: 'ACTION',
        key: 'action',
        align: 'center',
        render: (text, record) =>
            hsdEditingKey === record.key ? (
                <div>
                    <Button type="link" onClick={() => handleHsdSave(record.key)}>
                        <img src={saveIcon} alt="save" style={{ height: '24px' }} />
                    </Button>
                    <Button
                        type="link"
                        onClick={handleHsdCancel}
                        style={{ marginLeft: '-20px' }}
                    >
                        <img src={CancelIcon} alt="cancel" style={{ height: '24px' }} />
                    </Button>
                </div>
            ) : (
                <>
                    <Button type="link" onClick={() => handleHsdEdit(record.key)}>
                        <img src={editIcon} alt="edit" />
                    </Button>
                    <Button
                        type="link"
                        onClick={() => handleHsdReset(record.key)}
                        style={{ marginLeft: '-10px' }}
                    >
                        <img src={Reset} alt="reset" style={{ height: '16px' }} />
                    </Button>
                </>
            )
    };
    const ALGO_ACTION_COLUMN = {
        title: 'ACTION',
        key: 'action',
        align: 'center',
        render: (text, record) =>
            algoEditingKey === record.key ? (
                <div>
                    <Button type="link" onClick={() => handleAlgoSave(record.key)}>
                        <img src={saveIcon} alt="save" style={{ height: '24px' }} />
                    </Button>
                    <Button
                        type="link"
                        onClick={handleAlgoCancel}
                        style={{ marginLeft: '-20px' }}
                    >
                        <img src={CancelIcon} alt="cancel" style={{ height: '24px' }} />
                    </Button>
                </div>
            ) : (
                <>
                    <Button type="link" onClick={() => handleAlgoEdit(record.key)}>
                        <img src={editIcon} alt="edit" />
                    </Button>
                    <Button
                        type="link"
                        onClick={() => handleAlgoReset(record.key)}
                        style={{ marginLeft: '-10px' }}
                    >
                        <img src={Reset} alt="reset" style={{ height: '16px' }} />
                    </Button>
                </>
            )
    };

    // Prepare data sources with key
    const hsdData = [
        { key: 'hsd_scores', ...(weights[SLEEP_SIGNALS_WEIGHTING_TYPE.HSD_SCORES] || {}) }
    ];
    const algoData = [
        { key: 'sleep_algorithm', ...(weights[SLEEP_SIGNALS_WEIGHTING_TYPE.SLEEP_ALGORITHM] || {}) }
    ];

    return (
        <Container className='sleep_signals_hsd_weightings'>
            <div className="header-tool">
                <h1 className="page-title">Sleep Signals Weightings</h1>
            </div>
            <div className="cardList">
                <div>
                    <h2>HSD Scores Weightings</h2>
                    <Table
                        columns={[...columnsHSDScores, HSD_ACTION_COLUMN]}
                        dataSource={hsdData}
                        pagination={false}
                        style={{ width: '800px' }}
                    />
                </div>
                <div>
                    <h2>Sleep Algorithm Weightings</h2>
                    <Table
                        columns={[...columnsSleepAlgorithm, ALGO_ACTION_COLUMN]}
                        dataSource={algoData}
                        pagination={false}
                        style={{ width: '800px' }}
                    />
                </div>
            </div>
        </Container>
    )
}